import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Plus } from "lucide-react";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CarregandoLista } from "@/components/carregando";

import { semTaxaQueDeveria } from "../components/TaxaResumo";
import { ROTA_NOVA_FORMA, rotaEditarForma } from "../rotas";
import CanaisOnline from "./CanaisOnline";
import LinhaForma from "./LinhaForma";
import useFormas from "./useFormas";

// Contagem só depois de carregar (25/10/2026): antes mostrava "Ativas (0)" piscando.
function Abas({ aba, setAba, ativas, inativas, carregando }) {
  const item = (id, rotulo, n) => (
    <button key={id} type="button" onClick={() => setAba(id)} role="tab" aria-selected={aba === id}
      className={`h-8 px-3.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${aba === id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
      {rotulo}
      {carregando ? null : <span className={`text-[11px] tabular-nums px-1.5 rounded-full ${aba === id ? "bg-primary/10 text-primary" : "bg-muted-foreground/10"}`}>{n}</span>}
    </button>
  );
  return <div className="inline-flex rounded-lg bg-muted p-1 gap-1" role="tablist">{item("ativas", "Ativas", ativas)}{item("inativas", "Desativadas", inativas)}</div>;
}

function AvisoSemTaxa({ qtd }) {
  if (!qtd) return null;
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm flex items-start gap-2" data-e2e="aviso-sem-taxa">
      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <span><strong>{qtd} {qtd === 1 ? "forma costuma ter taxa e está" : "formas costumam ter taxa e estão"} sem taxa configurada.</strong> Sem ela, o DRE mostra mais lucro do que existe e o Fluxo de Caixa acha que o dinheiro cai na hora. Clique em "Definir taxa e prazo".</span>
    </div>
  );
}

// Financeiro → Formas de Pagamento. Editar/criar abre a tela inteira
// (editar/EditarFormaPage) — o popup saiu em v1.5.784.
export default function ListaFormasPage() {
  const navigate = useNavigate();
  const f = useFormas();
  const [aba, setAba] = useState("ativas");
  const [excluir, setExcluir] = useState(null);
  const ativas = f.formas.filter((pm) => pm.is_active);
  const visiveis = aba === "ativas" ? ativas : f.formas.filter((pm) => !pm.is_active);
  const semTaxa = [...ativas.map((pm) => f.taxaDe(pm.code)), ...f.taxas.canais].filter(semTaxaQueDeveria).length;

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-6xl space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Formas de Pagamento</h1>
          <p className="text-muted-foreground">Onde cada forma aparece, quanto ela custa de taxa e em quantos dias o dinheiro cai na conta.</p>
        </div>
        <div className="flex gap-2">
          {!f.carregando && f.formas.length === 0 && <Button variant="outline" onClick={f.inicializar}>Criar as formas padrão</Button>}
          <Button onClick={() => navigate(ROTA_NOVA_FORMA)} data-e2e="nova-forma"><Plus className="w-4 h-4 mr-2" />Nova forma de pagamento</Button>
        </div>
      </div>
      <AvisoSemTaxa qtd={semTaxa} />
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Abas aba={aba} setAba={setAba} ativas={ativas.length} inativas={f.formas.length - ativas.length} carregando={f.carregando && !f.formas.length} />
          <p className="text-xs text-muted-foreground">Toque num módulo para colocar ou tirar a forma dele.</p>
        </div>
        {/* Carregamento com cards (25/10/2026): linhas piscando só na primeira carga */}
        {f.carregando && !f.formas.length ? <CarregandoLista linhas={6} avatar={false} colunas={2} className="overflow-hidden rounded-xl border" />
          : !visiveis.length ? <p className="p-12 text-center text-muted-foreground rounded-xl border border-dashed">{aba === "ativas" ? "Nenhuma forma ativa. Clique em \"Nova forma de pagamento\"." : "Nenhuma forma desativada."}</p>
            : <div className="space-y-2">{visiveis.map((pm) => {
              const i = f.formas.findIndex((x) => x.id === pm.id);
              return (
                <LinhaForma key={pm.id} pm={pm} taxa={f.taxaDe(pm.code)} ativa={pm.is_active}
                  setas={{ podeSubir: i > 0 && f.formas[i - 1]?.is_active, podeDescer: i < f.formas.length - 1 && f.formas[i + 1]?.is_active, aoSubir: () => f.mover(i, -1), aoDescer: () => f.mover(i, 1) }}
                  acoes={{ editar: () => navigate(rotaEditarForma(pm.id)), modulo: (m) => f.alternarModulo(pm, m), ativar: () => f.alternarAtiva(pm), excluir: () => setExcluir(pm) }} />
              );
            })}</div>}
      </section>
      {f.carregando && !f.formas.length ? <CarregandoLista linhas={3} avatar={false} colunas={1} /> : <CanaisOnline canais={f.taxas.canais} />}

      <AlertDialog open={!!excluir} onOpenChange={(v) => !v && setExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir "{excluir?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Pedidos antigos continuam com a forma gravada. Se só quer tirar das telas, prefira desativar.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={async () => { await f.excluir(excluir); setExcluir(null); }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
